// IntegrationApp (Membrane) entry point for the launch wizard's Zephyr test-case import and the
// Integrations settings page, which connects and disconnects the Zephyr connectors. This hook
// owns the SDK client; the LiveView drives it by events. The client never holds a token: it calls
// `fetchToken` on demand and re-fetches as the 1h JWT nears expiry, so `fetchToken` round-trips to
// the server, which mints a fresh token. We reject the round-trip after a timeout so a dropped reply
// surfaces as a failed call instead of hanging the client forever.
//
// `@membranehq/sdk` is large, so it's dynamically imported and code-split into its own chunk that
// loads only when this hook mounts (the Scope step of a test-case run), not on every GT page.
//
// On mount the hook reports which Zephyr connections the organisation has, so the server shows only
// the source options whose connection is present. Then the LiveView drives the import: it asks the
// hook to load projects, then the test cycles for a project, then the executions for a cycle; the
// hook runs the matching IntegrationApp action (paginating to the end) and pushes the results back.
// Any token, connection, or action failure reports "none present" / a staged error rather than
// throwing, so the wizard degrades to a clear message instead of a dead picker. The one exception is
// the re-report after a connect or disconnect succeeds: if that fails, the page keeps what it showed.
const ZEPHYR_KEYS = ["zephyr-scale", "zephyr-essential-cloud"];
const TOKEN_TIMEOUT_MS = 10_000;
const EXECUTIONS_PER_PAGE = 10;
const MAX_PAGES = 1000;

const toOptions = records =>
  records
    .map(record => ({ value: record.fields.value, label: record.fields.label }))
    .sort((a, b) => a.label.localeCompare(b.label));

export default {
  async mounted() {
    this.registerActions();

    try {
      const { MembraneClient } = await import("@membranehq/sdk");

      this.client = new MembraneClient({
        fetchToken: () =>
          new Promise((resolve, reject) => {
            const timer = setTimeout(
              () => reject(new Error("integration app token fetch timed out")),
              TOKEN_TIMEOUT_MS,
            );

            this.pushEvent("zephyr:fetch_token", {}, reply => {
              clearTimeout(timer);
              if (reply && reply.token) resolve(reply.token);
              else reject(new Error("integration app token unavailable"));
            });
          }),
      });

      await this.reportConnections();
    } catch (_error) {
      this.pushEvent("zephyr:connections_reported", { keys: [] });
    }
  },

  async reportConnections() {
    const { items } = await this.client.connections.find();
    const present = ZEPHYR_KEYS.filter(key =>
      items.some(
        connection =>
          !connection?.disconnected && (connection?.integration?.key === key || connection?.integrationKey === key),
      ),
    );
    this.pushEvent("zephyr:connections_reported", { keys: present });
  },

  registerActions() {
    this.handleEvent("zephyr-load-projects", ({ key }) =>
      this.loadOptions(key, "get-projects", {}, "zephyr:projects_loaded", "projects"),
    );

    this.handleEvent("zephyr-load-cycles", ({ key, project_value }) =>
      this.loadOptions(key, "get-test-cycles", { project_value }, "zephyr:cycles_loaded", "cycles"),
    );

    this.handleEvent("zephyr-import", ({ key, project_value, test_cycle_value }) =>
      this.importExecutions(key, { project_value, test_cycle_value }),
    );

    this.handleEvent("zephyr-connect", ({ key }) => this.connect(key));
    this.handleEvent("zephyr-disconnect", ({ key }) => this.disconnect(key));
  },

  // The settings page connects a connector rather than importing from one: the SDK opens its own
  // window for the vendor's consent flow and resolves once it closes, so the page re-reports which
  // connections exist instead of assuming this one now does.
  async connect(key) {
    try {
      await this.client.integration(key).openNewConnection();
    } catch (_error) {
      this.pushEvent("zephyr:action_failed", { stage: "connect" });
      return;
    }

    await this.reportConnections().catch(() => {});
  },

  async disconnect(key) {
    try {
      await this.client.connection(key).archive();
    } catch (_error) {
      this.pushEvent("zephyr:action_failed", { stage: "disconnect" });
      return;
    }

    await this.reportConnections().catch(() => {});
  },

  async loadOptions(key, action, params, event, stage) {
    try {
      this.pushEvent(event, { options: toOptions(await this.fetchByCursor(key, action, params)) });
    } catch (_error) {
      this.pushEvent("zephyr:action_failed", { stage });
    }
  },

  async importExecutions(key, params) {
    try {
      this.pushEvent("zephyr:executions_loaded", { records: await this.fetchExecutions(key, params) });
    } catch (_error) {
      this.pushEvent("zephyr:action_failed", { stage: "import" });
    }
  },

  // get-projects / get-test-cycles paginate on the server-returned opaque cursor. MAX_PAGES guards a
  // connector that never returns a falsy cursor, so a stuck cursor fails the action instead of
  // spinning forever and exhausting memory.
  async fetchByCursor(key, action, params) {
    const all = [];
    let cursor = 0;

    for (let page = 0; page < MAX_PAGES; page++) {
      const { output } = await this.client
        .connection(key)
        .action(action)
        .run({ ...params, cursor: cursor.toString() });
      all.push(...output.records);
      if (!output.cursor) return all;
      cursor = output.cursor;
    }

    throw new Error("integration app pagination exceeded the page limit");
  },

  // get-test-executions paginates on a client-computed offset, gated on the server cursor.
  async fetchExecutions(key, params) {
    const all = [];
    let cursor = 0;

    for (let page = 0; page < MAX_PAGES; page++) {
      const { output } = await this.client
        .connection(key)
        .action("get-test-executions")
        .run({ ...params, records_per_page: EXECUTIONS_PER_PAGE.toString(), cursor: cursor.toString() });
      all.push(...output.records);
      if (!output.cursor) return all;
      cursor += EXECUTIONS_PER_PAGE;
    }

    throw new Error("integration app pagination exceeded the page limit");
  },
};
